import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/authSlice';
import { fetchCart } from '../../store/cartSlice';
import API_BASE_URL from '../../config';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showMobileOverlay, setShowMobileOverlay] = useState(false);

  const searchRef = useRef(null);
  const userMenuRef = useRef(null);

  const cartTotalQuantity = useSelector((state) => state.cart.totalQuantity);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCart());
    }
  }, [isAuthenticated, dispatch]);

  // ── Close user dropdown when clicking outside ──────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchResults(false);
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    setIsUserDropdownOpen(false);
    setIsMenuOpen(false);
    setShowMobileOverlay(false);
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setShowMobileOverlay(!showMobileOverlay);
    setIsUserDropdownOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
    setShowMobileOverlay(false);
  };

  // ── Search (debounced) ─────────────────────────────────────────────────────
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    const debounceTimer = setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/products?search=${encodeURIComponent(searchQuery.trim())}`
        );
        if (response.ok) {
          const data = await response.json();
          const results = data.data || data.products || data || [];
          setSearchResults(Array.isArray(results) ? results : []);
          setShowSearchResults(true);
        } else {
          setSearchResults([]);
          setShowSearchResults(true);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
        setShowSearchResults(true);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearchItemClick = (productId) => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setIsSearchOpen(false);
    navigate(`/products/${productId}`);
  };

  const handleSearchToggle = () => {
    setIsSearchOpen((prev) => {
      if (prev) {
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
      }
      return !prev;
    });
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getUserDisplayName = () => {
    if (user?.first_name) return user.first_name;
    if (user?.username) return user.username;
    return 'User';
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name)
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`;
    if (user?.first_name) return user.first_name.charAt(0);
    if (user?.username) return user.username.charAt(0).toUpperCase();
    return 'U';
  };

  const formatPrice = (price) => {
    if (!price) return '₹0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `₹${numPrice.toFixed(2)}`;
  };

  const getImageUrl = (url) => {
    if (!url) return '/assets/product-placeholder.jpg';
    if (url.startsWith('http')) {
      if (url.includes('/frontend/public')) return url.replace('/frontend/public', '');
      return url;
    }
    return url;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <header className="header">
      <div className="header-container">
        <div className="main-header">

          {/* Hamburger */}
          <button onClick={toggleMobileMenu} className="mobile-menu-toggle" aria-label="Menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>

          {/* Logo */}
          <Link to="/" className="logo">
            <img
              src="/assets/logo.png"
              alt="Vision Care Optics Logo"
              className="logo-image"
              onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/40x40?text=VC'; }}
            />
            <div className="logo-text-wrapper">
              <span className="logo-text">VISION<span className="logo-highlight">CARE</span></span>
              <span className="logo-subtext">OPTICS</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="main-nav">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/about" className="nav-link">About Us</Link>
            <Link to="/products" className="nav-link">Shop</Link>
            <Link to="/contact" className="nav-link">Contact Us</Link>
          </nav>

          {/* Right icons */}
          <div className="header-actions">

            {/* ── Desktop Search ── */}
            <div className="search-container desktop-only" ref={searchRef}>
              <button
                className="icon-button search-toggle"
                onClick={handleSearchToggle}
                aria-label="Search"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>

              {/* Dropdown renders OUTSIDE the header flow via fixed positioning */}
              {isSearchOpen && (
                <div className="search-dropdown">
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                    {searchLoading && <div className="search-spinner" />}
                  </div>

                  {showSearchResults && searchQuery.trim().length >= 2 && (
                    <div className="search-results">
                      {searchResults.length === 0 ? (
                        <div className="no-search-results">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.35, marginBottom: 6 }}>
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                          </svg>
                          <p>No products found for "<strong>{searchQuery}</strong>"</p>
                        </div>
                      ) : (
                        <>
                          {searchResults.slice(0, 5).map((product) => (
                            <div
                              key={product.id}
                              className="search-result-item"
                              onClick={() => handleSearchItemClick(product.id)}
                            >
                              <img
                                src={getImageUrl(product.images?.[0]?.image_url)}
                                alt={product.name}
                                onError={(e) => { e.target.src = '/assets/product-placeholder.jpg'; }}
                              />
                              <div className="search-result-info">
                                <h4>{product.name}</h4>
                                <p>{product.brand}</p>
                                <span className="search-result-price">
                                  {formatPrice(product.sale_price || product.price)}
                                </span>
                              </div>
                            </div>
                          ))}
                          {searchResults.length > 5 && (
                            <Link
                              to={`/products?search=${encodeURIComponent(searchQuery)}`}
                              className="view-all-results"
                              onClick={() => { setShowSearchResults(false); setIsSearchOpen(false); }}
                            >
                              View all {searchResults.length} results
                            </Link>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Wishlist */}
            <Link to="/wishlist" className="icon-button" aria-label="Wishlist">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </Link>

            {/* Cart */}
            <Link to="/cart" className="icon-button cart-button" aria-label="Cart">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              {cartTotalQuantity > 0 && <span className="cart-badge">{cartTotalQuantity}</span>}
            </Link>

            {/* ── Desktop User Menu ── */}
            {user ? (
              <div className="user-menu desktop-only" ref={userMenuRef}>
                <button
                  className="user-button"
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  aria-label="User menu"
                >
                  <div className="user-avatar">{getUserInitials()}</div>
                  <span className="user-name">{getUserDisplayName()}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>

                {isUserDropdownOpen && (
                  <div className="user-dropdown">
                    <div className="user-info-header">
                      <div className="user-info-avatar">{getUserInitials()}</div>
                      <div className="user-info-details">
                        <p className="user-info-name">{user.first_name} {user.last_name}</p>
                        <p className="user-info-email">{user.email}</p>
                      </div>
                    </div>
                    <div className="dropdown-divider" />
                    <Link to="/account" onClick={() => setIsUserDropdownOpen(false)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      My Account
                    </Link>
                    <Link to="/orders" onClick={() => setIsUserDropdownOpen(false)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h4"/></svg>
                      My Orders
                    </Link>
                    <Link to="/wishlist" onClick={() => setIsUserDropdownOpen(false)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                      Wishlist
                    </Link>
                    {user?.role === 'admin' && (
                      <>
                        <div className="dropdown-divider" />
                        <Link to="/admin" onClick={() => setIsUserDropdownOpen(false)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h4"/></svg>
                          Admin Dashboard
                        </Link>
                      </>
                    )}
                    <div className="dropdown-divider" />
                    <button onClick={handleLogout} className="logout-btn">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons desktop-only">
                <Link to="/login" className="btn-login">Sign In</Link>
                <Link to="/register" className="btn-register">Register</Link>
              </div>
            )}
          </div>
        </div>

        {/* ── Mobile overlay ── */}
        <div className={`mobile-overlay ${isMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu} />

        {/* ── Mobile sidebar ── */}
        <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-header">
            <img
              src="/assets/logo.png"
              alt="Logo"
              className="mobile-menu-logo"
              onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/40x40?text=VC'; }}
            />
            <div className="mobile-menu-title">
              <span>VISION<span>CARE</span></span>
              <small>OPTICS</small>
            </div>
            <button className="mobile-menu-close" onClick={closeMobileMenu}>✕</button>
          </div>

          {/* Mobile search */}
          <div className="mobile-search">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>

            {searchQuery.trim().length >= 2 && (
              <div className="mobile-search-results">
                {searchLoading ? (
                  <div className="search-loading">Searching...</div>
                ) : searchResults.length === 0 ? (
                  <div className="no-results">
                    No products found for "<strong>{searchQuery}</strong>"
                  </div>
                ) : (
                  searchResults.slice(0, 5).map((product) => (
                    <div
                      key={product.id}
                      className="mobile-search-item"
                      onClick={() => { handleSearchItemClick(product.id); closeMobileMenu(); }}
                    >
                      <img
                        src={getImageUrl(product.images?.[0]?.image_url)}
                        alt={product.name}
                        onError={(e) => { e.target.src = '/assets/product-placeholder.jpg'; }}
                      />
                      <div className="mobile-search-info">
                        <h4>{product.name}</h4>
                        <p>{product.brand}</p>
                        <span>{formatPrice(product.sale_price || product.price)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9L12 3L21 9L12 21L3 9Z"/></svg>
            Home
          </Link>
          <Link to="/about" className="mobile-nav-link" onClick={closeMobileMenu}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><circle cx="12" cy="8" r="0.5" fill="currentColor"/></svg>
            About Us
          </Link>
          <Link to="/products" className="mobile-nav-link" onClick={closeMobileMenu}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            Shop
          </Link>
          <Link to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            Contact Us
          </Link>

          <div className="mobile-menu-divider" />

          <Link to="/wishlist" className="mobile-nav-link" onClick={closeMobileMenu}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            Wishlist
          </Link>
          <Link to="/cart" className="mobile-nav-link" onClick={closeMobileMenu}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
            Cart {cartTotalQuantity > 0 && `(${cartTotalQuantity})`}
          </Link>

          {user ? (
            <>
              <div className="mobile-menu-divider" />
              <div className="mobile-user-info">
                <div className="mobile-user-avatar">{getUserInitials()}</div>
                <div className="mobile-user-details">
                  <p className="mobile-user-name">{user.first_name} {user.last_name}</p>
                  <p className="mobile-user-email">{user.email}</p>
                </div>
              </div>
              <Link to="/account" className="mobile-nav-link" onClick={closeMobileMenu}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                My Account
              </Link>
              <Link to="/orders" className="mobile-nav-link" onClick={closeMobileMenu}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h4"/></svg>
                My Orders
              </Link>
              {user?.role === 'admin' && (
                <Link to="/admin" className="mobile-nav-link" onClick={closeMobileMenu}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h4"/></svg>
                  Admin Dashboard
                </Link>
              )}
              <button onClick={() => { handleLogout(); closeMobileMenu(); }} className="mobile-logout-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Sign Out
              </button>
            </>
          ) : (
            <div className="mobile-auth">
              <Link to="/login" className="mobile-login" onClick={closeMobileMenu}>Sign In</Link>
              <Link to="/register" className="mobile-register" onClick={closeMobileMenu}>Create Account</Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;